import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import LandingShell from '@/components/landing/LandingShell';
import SEO from '@/components/common/SEO';
import { MAPLEOS_PRODUCTS } from '@/components/billing/mapleosProducts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, CheckCircle2, Download, HardDriveDownload, Monitor, Rocket, Wallet } from 'lucide-react';
import { useMarketplaceProducts } from '@/hooks/supabase/useMarketplaceProducts';
import { useMapleMarketLicenses } from '@/hooks/supabase/useMapleMarketLicenses';
import { useMapleMarketInstaller } from '@/OS/utils/MapleMarketInstaller';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { formatMapleMarketPrice } from '@/data/mapleMarketApps';
import { buildDesktopAppReturnUrl, isElectronCheckoutEnvironment } from '@/lib/electronStripe';
import { getProductsSeo } from '@/lib/seo/publicMetadata';

const INSTALL_STAGE_LABELS: Record<string, string> = {
  requesting_download: 'Requesting download',
  downloading_package: 'Downloading package',
  installing_files: 'Installing to Program Files',
  recording_install: 'Recording install',
  ready: 'Ready to run',
};

const formatCredits = (credits: number) => new Intl.NumberFormat('en-US').format(credits);

export default function Purchase() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const seo = getProductsSeo(location.pathname === '/products' ? '/products' : '/purchase');

  const { user } = useAuth();
  const { toast } = useToast();
  const { getProductById, products } = useMarketplaceProducts();
  const { installBundle, runInstalledModule } = useMapleMarketInstaller();
  const [installStage, setInstallStage] = useState<string | null>(null);
  const [installing, setInstalling] = useState(false);
  const [installedRootPath, setInstalledRootPath] = useState<string | null>(null);
  const [attemptedDesktopReturn, setAttemptedDesktopReturn] = useState(false);

  const checkoutState = searchParams.get('checkout');
  const purchaseType = searchParams.get('type');
  const marketplaceSku = searchParams.get('sku');
  const isElectron = isElectronCheckoutEnvironment();
  const isDesktopBrowserReturn = searchParams.get('desktop') === '1' && !isElectron;

  useEffect(() => {
    if (purchaseType !== 'credits') {
      return;
    }

    const params = new URLSearchParams();
    if (checkoutState) {
      params.set('checkout', checkoutState);
    }

    navigate(`/credits${params.toString() ? `?${params.toString()}` : ''}`, { replace: true });
  }, [checkoutState, navigate, purchaseType]);

  const marketplaceProduct = useMemo(
    () => products.find((product) => product.sku === marketplaceSku) || getProductById(marketplaceSku || ''),
    [getProductById, marketplaceSku, products]
  );

  const desktopReturnUrl = useMemo(() => {
    if (!isDesktopBrowserReturn) {
      return null;
    }

    const params = new URLSearchParams(searchParams);
    params.delete('desktop');
    return buildDesktopAppReturnUrl('/purchase', params);
  }, [isDesktopBrowserReturn, searchParams]);

  useEffect(() => {
    if (!desktopReturnUrl || attemptedDesktopReturn) {
      return;
    }

    setAttemptedDesktopReturn(true);
    window.location.assign(desktopReturnUrl);
  }, [attemptedDesktopReturn, desktopReturnUrl]);

  const ownedSkus = useMapleMarketLicenses(marketplaceProduct ? [marketplaceProduct.sku] : []);
  const isOwned = marketplaceProduct ? marketplaceProduct.isFree || ownedSkus.has(marketplaceProduct.sku) : false;

  const handleMarketplaceInstall = async () => {
    if (!marketplaceProduct) {
      return;
    }

    if (!user) {
      navigate('/auth');
      return;
    }

    if (marketplaceProduct.packageType === 'none' || !marketplaceProduct.packagePath) {
      toast({
        title: 'Bundle coming soon',
        description: 'This license is active, but its installable bundle has not been published yet.',
      });
      return;
    }

    try {
      setInstalling(true);
      setInstallStage('requesting_download');
      setInstalledRootPath(null);

      const { data, error } = await supabase.functions.invoke('request-marketplace-download', {
        body: { sku: marketplaceProduct.sku },
      });

      if (error || !data?.url || !data?.version) {
        throw new Error(error?.message || 'Unable to request signed download');
      }

      const installed = await installBundle({
        product: marketplaceProduct,
        signedUrl: data.url,
        version: data.version,
        onProgress: setInstallStage,
      });

      setInstalledRootPath(installed.rootPath);
      toast({
        title: 'Installed successfully',
        description: `${marketplaceProduct.name} is ready to run inside MapleOS.`,
      });
    } catch (error) {
      toast({
        title: 'Install failed',
        description: error instanceof Error ? error.message : 'Unable to install this purchase.',
        variant: 'destructive',
      });
    } finally {
      setInstalling(false);
      setInstallStage((current) => (current === 'ready' ? current : null));
    }
  };

  return (
    <LandingShell>
      <SEO {...seo} />

      <div className="container mx-auto max-w-7xl px-4 py-10">
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary">Annual editions</Badge>
          <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl">Choose your MapleOS edition</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Every edition includes web access and the desktop installer. Pro and Enterprise also include the bare metal installer.
          </p>
        </div>

        {desktopReturnUrl && (
          <div className="mx-auto mt-8 max-w-4xl">
            <Card className="border-primary/20 shadow-card">
              <CardContent className="flex flex-col gap-4 pt-6 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-semibold text-foreground">Return to MapleOS desktop</p>
                  <p className="text-sm text-muted-foreground">
                    We tried to reopen the desktop app automatically. If nothing happened, use the button to jump back into MapleOS.
                  </p>
                </div>
                <Button asChild>
                  <a href={desktopReturnUrl}>Open MapleOS desktop</a>
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {purchaseType === 'marketplace' && marketplaceProduct && (
          <div className="mx-auto mt-10 max-w-4xl">
            <Card className="overflow-hidden border-primary/20 shadow-card">
              <div className="grid gap-0 md:grid-cols-[260px_1fr]">
                <img src={marketplaceProduct.image} alt={marketplaceProduct.name} className="h-full min-h-[220px] w-full object-cover" />
                <div>
                  <CardHeader>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={checkoutState === 'success' ? 'default' : 'secondary'}>
                        {checkoutState === 'success' ? 'Purchase complete' : checkoutState === 'cancelled' ? 'Checkout cancelled' : 'Marketplace purchase'}
                      </Badge>
                      {isOwned && <Badge variant="outline">Owned</Badge>}
                    </div>
                    <CardTitle className="mt-3 text-2xl">{marketplaceProduct.name}</CardTitle>
                    <CardDescription>
                      {checkoutState === 'success'
                        ? 'Your Maple Market license is ready. Install the bundle now or return later from the product page.'
                        : checkoutState === 'cancelled'
                          ? 'You can return to checkout anytime from this product page.'
                          : 'Manage this Maple Market purchase from here.'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      <span>{marketplaceProduct.developer}</span>
                      <span>{formatMapleMarketPrice(marketplaceProduct.price)}</span>
                      <span>{marketplaceProduct.category}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{marketplaceProduct.description}</p>
                    <div className="flex flex-wrap gap-3">
                      {checkoutState === 'success' && isOwned && (
                        <Button onClick={() => void handleMarketplaceInstall()} disabled={installing}>
                          <Download className="mr-2 h-4 w-4" />
                          {installing ? INSTALL_STAGE_LABELS[installStage || 'requesting_download'] || 'Installing' : 'Install'}
                        </Button>
                      )}
                      {installedRootPath && (
                        <Button variant="outline" onClick={() => runInstalledModule({ productName: marketplaceProduct.name, rootPath: installedRootPath })}>
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          Run Now
                        </Button>
                      )}
                      <Button asChild variant="outline">
                        <Link to={`/market/app/${marketplaceProduct.id}`}>
                          View product page <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                    {installStage && (
                      <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
                        {INSTALL_STAGE_LABELS[installStage] || installStage}
                      </div>
                    )}
                  </CardContent>
                </div>
              </div>
            </Card>
          </div>
        )}

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {MAPLEOS_PRODUCTS.map((product) => (
            <Card key={product.id} className="relative overflow-hidden">
              <CardHeader className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <Badge variant={product.includesBareMetalInstaller ? 'default' : 'secondary'}>
                    {product.includesBareMetalInstaller ? 'Bare metal included' : 'Web + desktop'}
                  </Badge>
                  <div className="text-2xl font-semibold">{product.priceLabel}</div>
                </div>
                <div>
                  <CardTitle>{product.name}</CardTitle>
                  <CardDescription className="mt-2">{product.tagline}</CardDescription>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2"><Monitor className="h-4 w-4 text-primary" /> Web access included</li>
                  <li className="flex items-center gap-2"><Rocket className="h-4 w-4 text-primary" /> Desktop installer included</li>
                  <li className="flex items-center gap-2"><HardDriveDownload className="h-4 w-4 text-primary" /> {product.includesBareMetalInstaller ? 'Bare metal installer included' : 'Bare metal in Pro or Enterprise'}</li>
                  {product.bonusCredits ? (
                    <li className="flex items-center gap-2">
                      <Wallet className="h-4 w-4 text-primary" /> Includes {formatCredits(product.bonusCredits)} bonus AI credits
                    </li>
                  ) : null}
                </ul>
                <Button asChild className="w-full">
                  <Link to={`/products/${product.id}`}>
                    View details <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mx-auto mt-10 max-w-4xl">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-primary" />
                <CardTitle>AI credits are billed separately</CardTitle>
              </div>
              <CardDescription>
                MapleOS editions are purchased annually. Pro and Enterprise include a one-time bonus AI credit grant, and paid cloud AI usage continues on credits after that.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Trial credits are included to get started.</p>
                <p>Pro and Enterprise each add a one-time bonus credit grant when you purchase the edition.</p>
                <p>Top up only when you need more usage.</p>
                <p>Enable auto top-up if you want uninterrupted paid AI access.</p>
              </div>
              <Button asChild variant="outline" className="shrink-0">
                <Link to="/credits">Manage your credits</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </LandingShell>
  );
}